import {
  Autocomplete,
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
  AutocompleteProps,
} from "@mui/material";
import React, { SyntheticEvent, useState } from "react";

export type ModifiedAutoCompleteProps<T> = Omit<
  AutocompleteProps<
    T,
    false, // no support for multiple choice
    true,
    false // cannot use with FreeSolo flag
  >,
  "onChange" | "options"
>;

interface HybridAutoCompleteProps<T> {
  autoCompleteProps: ModifiedAutoCompleteProps<T>;
  options: [T, ...T[]]; // is never empty
  totalCount: number;
  initialOption: NonNullable<T>;
  optionsGetter: (q: string) => Promise<T[]>;
  onValueChange?: (
    e: SyntheticEvent,
    newValue: T | null,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<T>
  ) => void;
}

/**
 * Customized Autocomplete component for a mixture of server-side and client-side filtering.
 *
 * If the totalCount is larger than the passed number of options, the optionsGetter function is called
 * on input change to get options from a server, where the current user input is used as a query.
 *
 */
function HybridAutoComplete<T>(props: HybridAutoCompleteProps<T>) {
  const [options, setOptions] = useState<T[]>(props.options);
  const [value, setValue] = useState<NonNullable<T>>(props.initialOption);
  const [inputValue, setInputValue] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  const handleChange = (
    e: SyntheticEvent,
    newValue: NonNullable<T>,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<T>
  ) => {
    setValue(newValue);

    // we implement our own onChange handler, with which
    // the user can hook into value changes.
    if (typeof props.onValueChange === "function") {
      props.onValueChange(e, newValue, reason, details);
    }
  };
  const handleInputChange = (
    e: SyntheticEvent,
    newInputValue: string,
    reason: AutocompleteInputChangeReason
  ) => {
    // if the server did not provide all the options, get more according to input!
    setInputValue(newInputValue);

    if (props.totalCount > props.options.length && reason === "input") {
      setLoading(true);
      props
        .optionsGetter(newInputValue)
        .then((newOptions: T[]) => {
          setOptions(newOptions);
          setLoading(false);
        })
        .catch(() => {
          setOptions(props.options);
          setLoading(false);
        });
    }
  };

  return (
    <Autocomplete
      {...props.autoCompleteProps}
      disableClearable
      options={options}
      loading={loading}
      value={value}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
    />
  );
}

export default HybridAutoComplete;
